import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react'

type UnsavedChangesContextValue = {
  /** True while the active editor (e.g. the proposal) has unsaved changes. */
  isDirty: boolean
  /** Register/clear the dirty flag. Prefer useRegisterUnsavedChanges from an editor. */
  setDirty: (dirty: boolean) => void
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | undefined>(undefined)

/**
 * Holds a single app-wide "has unsaved changes" signal, lifted OUT of the page
 * that owns the edits (ProposalPage) so it survives across two things a page can't
 * see on its own:
 *
 *   1. Route navigation — the guard (useBlocker + beforeunload) is mounted once at
 *      the shell instead of inside the page, so it can't be torn down mid-navigation
 *      by the very unmount the navigation triggers.
 *   2. Store switch — StoreSwitcher remounts the proposal via key={storeId} WITHOUT
 *      a route change, so useBlocker never sees it. The switch UI reads this flag to
 *      confirm before discarding edits.
 *
 * The page publishes its dirty state via useRegisterUnsavedChanges; consumers
 * (the guard, the store switcher) read isDirty.
 */
export const UnsavedChangesProvider = ({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement => {
  const [isDirty, setIsDirty] = useState(false)
  const setDirty = useCallback((dirty: boolean) => setIsDirty(dirty), [])
  const value = useMemo(() => ({isDirty, setDirty}), [isDirty, setDirty])
  return <UnsavedChangesContext.Provider value={value}>{children}</UnsavedChangesContext.Provider>
}

/** Reads the unsaved-changes signal. Must be called inside <UnsavedChangesProvider>. */
export const useUnsavedChanges = (): UnsavedChangesContextValue => {
  const ctx = useContext(UnsavedChangesContext)
  if (ctx === undefined) {
    throw new Error('useUnsavedChanges must be used within an UnsavedChangesProvider')
  }
  return ctx
}

/**
 * Publishes an editor's dirty state to the shared signal for as long as the
 * editor is mounted, and clears it on unmount (so a remount — e.g. after a store
 * switch — starts clean). Does not touch the editor's own reconciliation state;
 * it only mirrors the boolean up.
 */
export const useRegisterUnsavedChanges = (dirty: boolean): void => {
  const {setDirty} = useUnsavedChanges()
  useEffect(() => {
    setDirty(dirty)
    return () => setDirty(false)
  }, [dirty, setDirty])
}
